"use client";

import { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { Plus, Trash2 } from "lucide-react";
import { addTeamMemberAction, removeTeamMemberAction } from "@/app/actions";
import type { TeamMember } from "@/lib/db";

const ROLE_HELP: Record<string, string> = {
  initiator: "can submit payouts for approval",
  approver: "can approve or reject payouts",
  admin: "can do both",
};

/**
 * The app's own team roster (no SSO yet): who can initiate payouts and who
 * can approve them. Distinct from Conduit's on-chain wallet signers below.
 */
export default function TeamBoard({ customerId, members }: { customerId: string; members: TeamMember[] }) {
  const router = useRouter();
  const [pending, start] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ name: "", email: "", role: "initiator" as TeamMember["role"] });

  function add() {
    setError(null);
    start(async () => {
      const res = await addTeamMemberAction({ customerId, ...form });
      if (res.ok) {
        setForm({ name: "", email: "", role: "initiator" });
        router.refresh();
      } else setError(res.error);
    });
  }

  return (
    <div className="mb-10 max-w-3xl">
      <div className="eyebrow mb-3" style={{ color: "var(--c-ink-soft)" }}>
        payout roles (in-app)
      </div>
      <div className="card p-6">
        <p className="mb-5 max-w-xl text-[13px] font-light" style={{ color: "var(--c-ink-soft)" }}>
          Fiat payouts have no native approval layer at Conduit, so the app enforces one: initiators submit,
          approvers release, and nobody can approve their own payout. Pick who you're acting as from the top-left
          switcher.
        </p>

        {members.length > 0 && (
          <table className="table mb-5">
            <thead>
              <tr>
                <th>member</th>
                <th>role</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id}>
                  <td>
                    <div className="font-normal">{m.name ?? m.email}</div>
                    <div className="text-[12px]" style={{ color: "var(--c-ink-soft)" }}>
                      {m.email}
                    </div>
                  </td>
                  <td>
                    <span className={m.role === "admin" ? "pill pill-dark" : "pill pill-cream"}>{m.role}</span>
                    <span className="ml-2 text-[12px] font-light" style={{ color: "var(--c-ink-soft)" }}>
                      {ROLE_HELP[m.role]}
                    </span>
                  </td>
                  <td className="text-right">
                    <button
                      className="btn btn-ghost !p-2"
                      title="Remove"
                      disabled={pending}
                      onClick={() =>
                        start(async () => {
                          await removeTeamMemberAction(m.id);
                          router.refresh();
                        })
                      }
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="label">Name</label>
            <input className="field w-[160px]" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <label className="label">Email</label>
            <input
              className="field w-[220px]"
              type="email"
              placeholder="person@company.com"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </div>
          <div>
            <label className="label">Role</label>
            <select
              className="field w-[130px]"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value as TeamMember["role"] })}
            >
              <option value="initiator">initiator</option>
              <option value="approver">approver</option>
              <option value="admin">admin</option>
            </select>
          </div>
          <button className="btn btn-dark" disabled={pending || !form.email.includes("@")} onClick={add}>
            <Plus size={14} /> Add member
          </button>
        </div>
        {error && (
          <p className="mt-3 text-[13px] font-light" style={{ color: "var(--c-danger)" }}>
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
